import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import {
  WhatsApp as WhatsAppIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import invitationService from "../service/invitationService";
import { buildWhatsappShareLink, formatDate } from "../utils/invitationHelpers";

const InvitationView = () => {
  const { invitationId } = useParams();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await invitationService.getPublicInvitation(invitationId);
        setInvitation(data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [invitationId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !invitation) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          textAlign: "center",
          p: 3,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          {error || "Invitation not found"}
        </Typography>
      </Box>
    );
  }

  const { invitationDetails, template, images, share } = invitation;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundImage: images?.coverImage
          ? `url(${images.coverImage})`
          : "none",
        backgroundColor: template?.primaryColor || "#D4AF37",
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        p: 3,
        color: template?.secondaryColor || "#FFFFFF",
      }}
    >
      <Box
        sx={{
          bgcolor: "rgba(0,0,0,0.35)",
          borderRadius: 3,
          p: { xs: 3, sm: 5 },
          maxWidth: 480,
        }}
      >
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {invitationDetails.groomName} & {invitationDetails.brideName}
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
          {invitationDetails.invitationMessage}
        </Typography>
        <Typography variant="h6">
          {formatDate(invitationDetails.eventDate)}
        </Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {invitationDetails.startTime}
        </Typography>
        <Typography variant="body1" fontWeight="bold">
          {invitationDetails.venueName}
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          {invitationDetails.venueAddress}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {invitationDetails.googleMapLink && (
            <Button
              variant="outlined"
              href={invitationDetails.googleMapLink}
              target="_blank"
              startIcon={<LocationIcon />}
              sx={{ color: "inherit", borderColor: "currentColor" }}
            >
              View on Map
            </Button>
          )}
          <Button
            variant="contained"
            color="success"
            startIcon={<WhatsAppIcon />}
            href={buildWhatsappShareLink(
              share?.whatsappMessage,
              share?.invitationUrl,
            )}
            target="_blank"
            rel="noopener noreferrer"
          >
            Share on WhatsApp
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default InvitationView;
